import { useCallback, useEffect, useMemo, useState } from 'react';

import { fetchTodayAppointmentDeliveries } from '../services/apis/dispense/todayAppointmentDeliveries';

const normalizeLookupValue = (value) => String(value || '').trim().toLowerCase();

const normalizePhn = (value) => String(value || '').replace(/\D/g, '');

const addAppointmentLookup = (map, key, appointmentDelivery) => {
	if (!key || key.endsWith(':') || map.has(key)) return;
	map.set(key, appointmentDelivery);
};

const getAppointmentPatientId = (appointmentDelivery) => appointmentDelivery?.pharmacy_patient?.id;

const getAppointmentPatientPhn = (appointmentDelivery) => (
	appointmentDelivery?.pharmacy_patient?.phn ||
	appointmentDelivery?.pharmacy_patient?.health_card_number ||
	appointmentDelivery?.clinic_patient?.phn ||
	appointmentDelivery?.clinic_patient?.health_card_number
);

const getRoutePatientPhn = (patient) => (
	patient?.phn ||
	patient?.health_card_number ||
	patient?.healthcard_number ||
	patient?.patients_win_rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.phn ||
	patient?.patients_win_rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.health_card_number ||
	patient?.patients_win_rx_data_today_missed?.find((rx) => rx?.phn || rx?.health_card_number)?.phn ||
	patient?.patients_win_rx_data_today_missed?.find((rx) => rx?.phn || rx?.health_card_number)?.health_card_number ||
	patient?.patients_win_rx_data_final_missed?.find((rx) => rx?.phn || rx?.health_card_number)?.phn ||
	patient?.patients_win_rx_data_final_missed?.find((rx) => rx?.phn || rx?.health_card_number)?.health_card_number ||
	patient?.rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.phn ||
	patient?.rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.health_card_number ||
	patient?.active_rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.phn ||
	patient?.active_rx_data?.find((rx) => rx?.phn || rx?.health_card_number)?.health_card_number
);

const buildAppointmentDeliveryMap = (appointmentDeliveries) => {
	const map = new Map();

	appointmentDeliveries.forEach((appointmentDelivery) => {
		const patientId = getAppointmentPatientId(appointmentDelivery);
		const phn = normalizePhn(getAppointmentPatientPhn(appointmentDelivery));

		addAppointmentLookup(map, `id:${normalizeLookupValue(patientId)}`, appointmentDelivery);
		addAppointmentLookup(map, `phn:${phn}`, appointmentDelivery);
	});

	return map;
};

const useTodayAppointmentDeliveryMap = ({ pharmacyId, enabled = true, refreshKey = '' }) => {
	const [appointmentDeliveries, setAppointmentDeliveries] = useState([]);

	useEffect(() => {
		let ignoreResult = false;

		if (!enabled || !pharmacyId) {
			setAppointmentDeliveries([]);
			return () => {
				ignoreResult = true;
			};
		}

		fetchTodayAppointmentDeliveries(pharmacyId)
			.then((data) => {
				if (ignoreResult) return;
				setAppointmentDeliveries(Array.isArray(data?.patients) ? data.patients : []);
			})
			.catch(() => {
				if (ignoreResult) return;
				setAppointmentDeliveries([]);
			});

		return () => {
			ignoreResult = true;
		};
	}, [enabled, pharmacyId, refreshKey]);

	const appointmentDeliveryMap = useMemo(() => (
		buildAppointmentDeliveryMap(appointmentDeliveries)
	), [appointmentDeliveries]);

	const getMatchedAppointmentDelivery = useCallback((patient) => {
		const patientId = patient?.id;
		const phn = normalizePhn(getRoutePatientPhn(patient));

		return (
			appointmentDeliveryMap.get(`id:${normalizeLookupValue(patientId)}`) ||
			appointmentDeliveryMap.get(`phn:${phn}`) ||
			null
		);
	}, [appointmentDeliveryMap]);

	return {
		getMatchedAppointmentDelivery,
	};
};

export default useTodayAppointmentDeliveryMap;
